<template>
  <b-modal
    v-model="modalShow"
    size="lg"
    centered
    @hidden="$emit('modal-closed')"
  >
    <template #modal-title>
      Update Cheque <span class="text-primary">( Cheque Number = {{ details.formatted_cheque_number }} )</span>
    </template>
    <div>
      <b-form @submit.prevent="submitHandler">
        <b-row>
          <b-col cols="6">
            <b-form-group
              label="Status"
            >
              <b-form-select
                v-model="details.status"
                :options="statusOptions"
                @change="onStatusChange"
              />
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Issue Date"
            >
              <b-form-datepicker
                v-model="details.issue_date"
                menu-class="w-100"
                calendar-width="100%"
              />
            </b-form-group>
          </b-col>

          <b-col
            v-if="details.status === 'deposited'"
            cols="6"
          >
            <b-form-group
              label="Deposited On"
              :state="depositedOnValidationState"
            >
              <b-form-datepicker
                v-model="details.deposited_on"
                menu-class="w-100"
                calendar-width="100%"
                :state="depositedOnValidationState"
              />
              <b-form-invalid-feedback>
                {{ depositedOnErrorMessage }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col
            v-if="details.status === 'cancelled'"
            cols="6"
          >
            <b-form-group
              label="Cancelled On"
            >
              <b-form-datepicker
                v-model="details.cancelled_on"
                menu-class="w-100"
                calendar-width="100%"
              />
            </b-form-group>
          </b-col>
          <b-col
            v-if="details.status === 'bounced'"
            cols="6"
          >
            <b-form-group
              label="Bounced On"
            >
              <b-form-datepicker
                v-model="details.bounced_on"
                menu-class="w-100"
                calendar-width="100%"
              />
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Amount"
              :state="amountValidationState"
            >
              <b-form-input
                v-model="details.amount"
                type="number"
                step="0.01"
                placeholder="Enter amount"
                :state="amountValidationState"
              />
              <b-form-invalid-feedback>
                {{ amountErrorMessage }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <b-col cols="6">
            <b-form-group
              label="Upto Amount"
              :state="uptoAmountValidationState"
            >
              <b-form-input
                v-model="details.upto_amount"
                type="number"
                step="0.01"
                placeholder="Enter upto amount"
                :state="uptoAmountValidationState"
              />
              <b-form-invalid-feedback>
                {{ uptoAmountErrorMessage }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>
          <b-col cols="6">
            <b-form-group
              label="Supplier"
            >
              <custom-v-select
                id="supplier"
                v-model="details.supplier"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="supplierOptions"
                label="text"
              />
              <p
                v-if="fieldErrors && fieldErrors.supplier"
                class="error-message"
              >
                {{ fieldErrors.supplier[0] }}
              </p>
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              label-for="remarks"
              :state="remarksValidationState"
            >
              <template #label>
                Remarks
                <span
                  v-if="details.status === 'cancelled'"
                  class="text-danger"
                >*</span>
              </template>
              <b-form-textarea
                v-model="remarks"
                rows="3"
                placeholder="Enter Remark"
                :state="remarksValidationState"
                :class="{ 'error-border': remarksValidationState === false }"
              />
              <b-form-invalid-feedback>
                {{ remarksErrorMessage }}
              </b-form-invalid-feedback>
            </b-form-group>
          </b-col>

          <!-- Sent Through (editable) -->
          <b-col cols="6">
            <b-form-group
              label="Sent Through"
              label-for="sentThrough"
            >
              <custom-v-select
                id="sentThrough"
                v-model="sentThroughSelected"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="sentThroughOptions"
                label="text"
                @input="onSentThroughChange"
              />
              <p
                v-if="fieldErrors && fieldErrors.sent_through"
                class="error-message"
              >
                {{ fieldErrors.sent_through[0] }}
              </p>
            </b-form-group>
          </b-col>

          <!-- Courier related fields -->
          <template v-if="sentThroughSelected && sentThroughSelected.value === 'courier'">
            <b-col cols="6">
              <b-form-group
                label="Courier"
                label-for="courier"
              >
                <custom-v-select
                  id="courier"
                  v-model="details.courier"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="courierOptions"
                  label="text"
                />
                <p
                  v-if="fieldErrors && fieldErrors.courier"
                  class="error-message"
                >
                  {{ fieldErrors.courier[0] }}
                </p>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Courier Date"
                label-for="courier-date"
              >
                <b-form-datepicker
                  id="courier-date"
                  v-model="details.courier_date"
                  menu-class="w-100"
                  calendar-width="100%"
                />
                <p
                  v-if="fieldErrors && fieldErrors.courier_date"
                  class="error-message"
                >
                  {{ fieldErrors.courier_date[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="Delivery Boy"
                label-for="deliveryBoy"
              >
                <custom-v-select
                  id="deliveryBoy"
                  v-model="details.delivery_boy"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :options="deliveryBoyOptions"
                  label="text"
                />
                <p
                  v-if="fieldErrors && fieldErrors.delivery_boy"
                  class="error-message"
                >
                  {{ fieldErrors.delivery_boy[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="Courier Slip"
                label-for="courierSlip"
              >
                <!-- Show current file info if exists -->
                <div
                  v-if="currentCourierSlipUrl"
                  class="d-flex justify-content-between align-items-center"
                >
                  <label class="form-label text-muted mb-2">Current File:</label>
                  <div class="d-flex align-items-center justify-content-between">
                    <div>
                      <b-button
                        variant="outline-primary"
                        size="sm"
                        @click="viewCurrentFile"
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="14"
                          class="mr-1"
                        />
                        View
                      </b-button>
                      <b-button
                        variant="outline-danger"
                        size="sm"
                        class="ml-2"
                        @click="removeCurrentFile"
                      >
                        <feather-icon
                          icon="XIcon"
                          size="14"
                          class="mr-1"
                        />
                        Remove
                      </b-button>
                    </div>
                  </div>
                </div>

                <!-- File input for new upload -->
                <div>
                  <b-form-file
                    id="courierSlip"
                    v-model="newCourierSlipFile"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    placeholder="Choose courier slip file..."
                    drop-placeholder="Drop file here..."
                  />
                </div>

                <!-- Error message -->
                <p
                  v-if="fieldErrors && fieldErrors.courier_slip"
                  class="error-message mt-2"
                >
                  {{ fieldErrors.courier_slip[0] }}
                </p>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group
                label="Docket Number"
                label-for="docketNumber"
              >
                <b-form-input
                  id="docketNumber"
                  v-model="details.docket_number"
                  type="text"
                  placeholder="Docket Number"
                />
                <p
                  v-if="errorMessage && errorMessage.docket_number"
                  class="error-message"
                >
                  {{ errorMessage.docket_number[0] }}
                </p>
              </b-form-group>
            </b-col>
          </template>

          <!-- MR related fields -->
          <template v-if="sentThroughSelected && sentThroughSelected.value === 'MR'">
            <b-col cols="6">
              <b-form-group
                label="MR Name"
                label-for="mr"
                :state="mrValidationState"
              >
                <b-form-input
                  id="mr"
                  v-model="details.mr"
                  type="text"
                  placeholder="Enter MR Name"
                  :state="mrValidationState"
                  @input="onMrNameInput"
                />
                <b-form-invalid-feedback>
                  {{ mrErrorMessage }}
                </b-form-invalid-feedback>
                <p
                  v-if="fieldErrors && fieldErrors.mr"
                  class="error-message"
                >
                  {{ fieldErrors.mr[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="MR Mobile No."
                label-for="mr_mobile"
                :state="mrMobileValidationState"
              >
                <b-form-input
                  id="mr_mobile"
                  v-model="details.mr_mobile"
                  type="text"
                  placeholder="Enter 10 digit mobile number"
                  maxlength="10"
                  :state="mrMobileValidationState"
                  @input="onMrMobileInput"
                />
                <b-form-invalid-feedback>
                  {{ mrMobileErrorMessage }}
                </b-form-invalid-feedback>
                <p
                  v-if="fieldErrors && fieldErrors.mr_mobile"
                  class="error-message"
                >
                  {{ fieldErrors.mr_mobile[0] }}
                </p>
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="MR Handover Date"
                label-for="mr-handover-date"
              >
                <b-form-datepicker
                  id="mr-handover-date"
                  v-model="details.mr_handover_date"
                  menu-class="w-100"
                  calendar-width="100%"
                />
                <p
                  v-if="fieldErrors && fieldErrors.mr_handover_date"
                  class="error-message"
                >
                  {{ fieldErrors.mr_handover_date[0] }}
                </p>
              </b-form-group>
            </b-col>
          </template>
        </b-row>
      </b-form>

      <b-alert
        variant="danger"
        :show="errorMessage ? true : false"
      >
        <div class="alert-body">
          <p>
            {{ errorMessage }}
          </p>
        </div>
      </b-alert>
    </div>

    <template #modal-footer="{ ok, cancel }">
      <b-button
        variant="secondary"
        @click="cancel()"
      >
        Cancel
      </b-button>
      <b-button
        variant="primary"
        :disabled="submitting"
        @click="submitHandler"
      >
        Update
        <b-spinner
          v-if="submitting"
          small
          label="Small Spinner"
        />
      </b-button>

    </template>
  </b-modal>
</template>

<script>
import {
  BButton, BSpinner, BForm, BFormGroup, BAlert,
  BFormInput, BFormSelect, BFormDatepicker, BFormTextarea,
  BRow, BCol, BModal, BFormInvalidFeedback,
  BFormFile,
} from 'bootstrap-vue'
import axios from 'axios'

import downloadFileMixin from '@/mixins/downloadFileMixin'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import CustomVSelect from '../UI/CustomVSelect.vue'

export default {
  components: {
    CustomVSelect,
    BButton,
    BSpinner,
    BFormGroup,
    BForm,
    BFormInput,
    BFormSelect,
    BFormDatepicker,
    BFormTextarea,
    BAlert,
    BRow,
    BCol,
    BModal,
    BFormFile,
    BFormInvalidFeedback,
  },
  mixins: [downloadFileMixin],
  props: {
    id: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      modalShow: true,
      submitting: false,
      errorMessage: null,
      fieldErrors: {}, // Add this for field-specific errors
      remarks: '',
      showValidationErrors: false,
      supplierOptions: [],
      selectedCourier: null,
      courierOptions: [],
      deliveryBoyOptions: [],
      newCourierSlipFile: null, // For new file uploads
      currentCourierSlipUrl: null, // For existing file URL
      courierSlipToRemove: false, // Flag to track if current file should be removed

      // Sent Through (editable)
      sentThroughSelected: null,
      sentThroughOptions: [
        { value: 'courier', text: 'Courier' },
        { value: 'MR', text: 'MR' },
      ],

      details: {
        status: 'undeposited',
        supplier: null,
        issue_date: '',
        deposited_on: null,
        cancelled_on: null,
        bounced_on: null,
        docket_number: null,
        amount: 0,
        upto_amount: 0,
        delivery_boy: null,
        courier_slip: null,
        courier: null,
        courier_date: null,
        sent_through: null,
        mr: null,
        mr_mobile: null,
        mr_handover_date: null,
      },
      statusOptions: [
        { value: 'undeposited', text: 'Undeposited' },
        { value: 'deposited', text: 'Deposited' },
        { value: 'bounced', text: 'Bounced' },
        { value: 'security', text: 'Security' },
        { value: 'cancelled', text: 'Cancelled' },
      ],
    }
  },
  computed: {
    amountValidationState() {
      // Check for server-side errors first
      if (this.fieldErrors && this.fieldErrors.amount) {
        return false
      }
      // Then check client-side validation for deposited status
      if (this.showValidationErrors && this.details.status === 'deposited') {
        return !!(this.details.amount && this.details.amount > 0)
      }
      return null
    },
    amountErrorMessage() {
      // Show server-side error first
      if (this.fieldErrors && this.fieldErrors.amount) {
        return this.fieldErrors.amount[0]
      }
      // Then show client-side validation error
      if (this.showValidationErrors && this.details.status === 'deposited' && (!this.details.amount || this.details.amount <= 0)) {
        return 'Amount must be greater than zero for deposited cheques'
      }
      return ''
    },
    uptoAmountValidationState() {
      // Check for server-side errors
      if (this.fieldErrors && this.fieldErrors.upto_amount) {
        return false
      }
      return null
    },
    uptoAmountErrorMessage() {
      // Show server-side error
      if (this.fieldErrors && this.fieldErrors.upto_amount) {
        return this.fieldErrors.upto_amount[0]
      }
      return ''
    },
    depositedOnValidationState() {
      if (this.showValidationErrors && this.details.status === 'deposited') {
        return !!this.details.deposited_on
      }
      return null
    },
    depositedOnErrorMessage() {
      if (this.showValidationErrors && this.details.status === 'deposited' && !this.details.deposited_on) {
        return 'Deposited date is required for deposited cheques'
      }
      return ''
    },
    remarksValidationState() {
      if (this.showValidationErrors && this.details.status === 'cancelled') {
        return !!(this.remarks && this.remarks.trim().length > 0)
      }
      return null // No validation for other statuses
    },
    remarksErrorMessage() {
      if (this.showValidationErrors && this.details.status === 'cancelled' && (!this.remarks || this.remarks.trim().length === 0)) {
        return 'Remarks are required when status is cancelled'
      }
      return ''
    },
    mrValidationState() {
      if (this.showValidationErrors && this.sentThroughSelected && this.sentThroughSelected.value === 'MR') {
        return !!(this.details.mr && this.details.mr.trim().length > 0)
      }
      return null
    },
    mrErrorMessage() {
      if (this.showValidationErrors && this.sentThroughSelected && this.sentThroughSelected.value === 'MR' && (!this.details.mr || this.details.mr.trim().length === 0)) {
        return 'MR Name is required'
      }
      return ''
    },
    mrMobileValidationState() {
      if (this.showValidationErrors && this.sentThroughSelected && this.sentThroughSelected.value === 'MR') {
        return !!(this.details.mr_mobile && this.details.mr_mobile.length === 10 && /^\d{10}$/.test(this.details.mr_mobile))
      }
      return null
    },
    mrMobileErrorMessage() {
      if (this.showValidationErrors && this.sentThroughSelected && this.sentThroughSelected.value === 'MR') {
        if (!this.details.mr_mobile || this.details.mr_mobile.length === 0) {
          return 'Mobile number is required'
        }
        if (this.details.mr_mobile.length !== 10 || !/^\d{10}$/.test(this.details.mr_mobile)) {
          return 'Mobile number must be exactly 10 digits'
        }
      }
      return ''
    },
  },
  mounted() {
    this.fetchDetails()
  },
  methods: {
    // Clear field errors when user starts typing or changes values
    clearFieldError(fieldName) {
      if (this.fieldErrors && this.fieldErrors[fieldName]) {
        this.$delete(this.fieldErrors, fieldName)
      }
    },

    async fetchDetails() {
      try {
        // Fetch vatika cheques details
        const supplierRes = await axios.get('/supplier_options')
        this.supplierOptions = supplierRes.data.data
        const courierRes = await axios.get('/returns/courier_options')
        this.courierOptions = courierRes.data.data
        const deliveryBoyRes = await axios.get('/accounts/salesmans_options')
        this.deliveryBoyOptions = deliveryBoyRes.data.data
        const detailsRes = await axios.get(`/accounts/vatika_cheques/${this.id}`)
        const selectedSupplier = supplierRes.data?.data?.find(item => item.id === detailsRes.data.supplier)
        const selectedCourier = courierRes.data?.data?.find(item => item.id === detailsRes.data.courier)
        const selectedDeliveryBoy = deliveryBoyRes.data?.data?.find(item => item.id === detailsRes.data.delivery_boy)

        this.details = {
          ...detailsRes.data,
          supplier: selectedSupplier,
          courier: selectedCourier,
          delivery_boy: selectedDeliveryBoy,
        }

        // Prepopulate sent_through dropdown from fetched details
        this.sentThroughSelected = this.sentThroughOptions.find(
          option => option.value === detailsRes.data.sent_through,
        ) || null

        // Prepopulate remarks from fetched details
        this.remarks = detailsRes.data.remarks || ''

        // Handle courier slip URL - check if it's a string URL
        if (detailsRes.data.courier_slip && typeof detailsRes.data.courier_slip === 'string') {
          this.currentCourierSlipUrl = detailsRes.data.courier_slip
          this.details.courier_slip = null // Clear the file input
        }

        // Fetch supplier options
      } catch (error) {
        this.errorMessage = error?.response?.data?.detail || 'Failed to fetch details!'
      }
    },

    // Method to view current file
    viewCurrentFile() {
      if (this.currentCourierSlipUrl) {
        window.open(this.currentCourierSlipUrl, '_blank')
      }
    },

    // Method to remove current file
    removeCurrentFile() {
      this.currentCourierSlipUrl = null
      this.courierSlipToRemove = true
      this.newCourierSlipFile = null
    },

    // Method to get filename from URL
    getFileName(url) {
      if (!url) return 'Unknown file'
      const parts = url.split('/')
      const filename = parts[parts.length - 1]
      // Decode URL encoding and limit length
      const decoded = decodeURIComponent(filename)
      return decoded.length > 30 ? `${decoded.substring(0, 30)}...` : decoded
    },

    onStatusChange() {
      // Reset conditional fields when status changes
      this.showValidationErrors = false // Reset validation errors when status changes
      this.fieldErrors = {} // Clear field errors when status changes

      if (this.details.status !== 'deposited') {
        this.details.deposited_on = null
      }
      if (this.details.status !== 'cancelled') {
        this.details.cancelled_on = null
      }
      if (this.details.status !== 'bounced') {
        this.details.bounced_on = null
      }

      // Reset file upload state
      this.newCourierSlipFile = null
      this.courierSlipToRemove = false
    },

    // Reset fields of the opposite branch whenever sent_through changes
    onSentThroughChange() {
      this.showValidationErrors = false
      this.fieldErrors = {}

      const selectedValue = this.sentThroughSelected ? this.sentThroughSelected.value : null

      if (selectedValue !== 'courier') {
        // Clear courier-related fields
        this.details.courier = null
        this.details.courier_date = null
        this.details.delivery_boy = null
        this.details.docket_number = null
        this.newCourierSlipFile = null
        this.currentCourierSlipUrl = null
        this.courierSlipToRemove = true
      }

      if (selectedValue !== 'MR') {
        // Clear MR-related fields
        this.details.mr = null
        this.details.mr_mobile = null
        this.details.mr_handover_date = null
      }
    },

    onMrNameInput(value) {
      // Convert to uppercase automatically
      this.details.mr = value ? value.toUpperCase() : value
    },

    onMrMobileInput(value) {
      // Only allow numeric input and limit to 10 digits
      const numericValue = (value || '').replace(/\D/g, '')
      this.details.mr_mobile = numericValue.slice(0, 10)
    },

    submitHandler() {
      this.submitting = true
      this.errorMessage = null // Clear previous errors
      this.fieldErrors = {} // Clear previous field errors
      this.showValidationErrors = true // Show validation errors on submit

      // Validate amount and deposited date for deposited status
      if (this.details.status === 'deposited') {
        if (!this.details.amount || this.details.amount <= 0) {
          this.submitting = false
          return // Error will be shown via computed property
        }

        if (!this.details.deposited_on) {
          this.submitting = false
          return // Error will be shown via computed property
        }
      }

      // Validate remarks for cancelled status
      if (this.details.status === 'cancelled') {
        if (!this.remarks || this.remarks.trim().length === 0) {
          this.submitting = false
          return // Error will be shown via computed property
        }
      }

      // Validate supplier selection
      if (!this.details.supplier) {
        this.errorMessage = 'supplier should be selected'
        this.submitting = false
        return // Error will be shown via computed property
      }

      // Validate MR fields if MR is selected
      if (this.sentThroughSelected && this.sentThroughSelected.value === 'MR') {
        if (!this.details.mr || this.details.mr.trim().length === 0) {
          this.submitting = false
          return
        }
        if (!this.details.mr_mobile || this.details.mr_mobile.length !== 10 || !/^\d{10}$/.test(this.details.mr_mobile)) {
          this.submitting = false
          return
        }
      }

      // Create FormData object
      const formData = new FormData()

      // Add basic fields
      formData.append('status', this.details.status)
      formData.append('amount', this.details.amount)
      formData.append('upto_amount', this.details.upto_amount)
      formData.append('issue_date', this.details.issue_date)
      formData.append('remarks', this.remarks)
      formData.append('supplier', this.details.supplier.id)

      // Add conditional fields based on status
      if (this.details.status === 'deposited' && this.details.deposited_on) {
        formData.append('deposited_on', this.details.deposited_on)
      }

      if (this.details.status === 'cancelled' && this.details.cancelled_on) {
        formData.append('cancelled_on', this.details.cancelled_on)
      }

      if (this.details.status === 'bounced' && this.details.bounced_on) {
        formData.append('bounced_on', this.details.bounced_on)
      }

      // Sent Through — always send, even when null, so backend can clear it
      const sentThroughValue = this.sentThroughSelected ? this.sentThroughSelected.value : ''
      formData.append('sent_through', sentThroughValue)

      // Courier branch
      if (sentThroughValue === 'courier') {
        if (this.details.courier) {
          formData.append('courier', this.details.courier.id)
        }

        if (this.details.delivery_boy) {
          formData.append('delivery_boy', this.details.delivery_boy.id)
        }

        if (this.details.courier_date) {
          formData.append('courier_date', this.details.courier_date)
        }

        // Handle courier slip file upload
        if (this.newCourierSlipFile instanceof File) {
          // New file selected
          formData.append('courier_slip', this.newCourierSlipFile)
        } else if (this.courierSlipToRemove) {
          // Mark for removal (send empty string or null)
          formData.append('courier_slip', '')
        }
        // If neither new file nor removal, keep existing file (don't append anything)
        if (this.details.docket_number) {
          formData.append('docket_number', this.details.docket_number)
        }
        // Null out MR-related fields
        formData.append('mr', '')
        formData.append('mr_mobile', '')
        formData.append('mr_handover_date', '')
      } else if (sentThroughValue === 'MR') {
        // MR branch
        formData.append('mr', this.details.mr || '')
        formData.append('mr_mobile', this.details.mr_mobile || '')
        if (this.details.mr_handover_date) {
          formData.append('mr_handover_date', this.details.mr_handover_date)
        } else {
          formData.append('mr_handover_date', '')
        }

        // Null out courier-related fields
        formData.append('courier', '')
        formData.append('courier_date', '')
        formData.append('delivery_boy', '')
        formData.append('courier_slip', '')
        formData.append('docket_number', '')
      } else {
        // Neither branch selected — null out everything
        formData.append('courier', '')
        formData.append('courier_date', '')
        formData.append('delivery_boy', '')
        formData.append('courier_slip', '')
        formData.append('docket_number', '')
        formData.append('mr', '')
        formData.append('mr_mobile', '')
        formData.append('mr_handover_date', '')
      }

      // Make the API call with FormData
      axios.patch(`/accounts/vatika_cheques/${this.id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(res => {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: res.data.message || 'Cheque Details Updated',
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
          this.$emit('updated')
          this.modalShow = false
          this.submitting = false
        }).catch(error => {
          this.submitting = false

          // Handle field-specific errors
          if (error.response && error.response.data && typeof error.response.data === 'object') {
            // Check if the response contains field-specific errors
            const responseData = error.response.data

            // If the response has field errors (like your JSON example)
            if (responseData.amount || responseData.upto_amount || responseData.supplier) {
              this.fieldErrors = responseData
            } else {
              // General error message
              this.errorMessage = responseData.detail || 'Failed to update details!'
            }
          } else {
            this.errorMessage = 'Failed to update details!'
          }
        })
    },
  },
}
</script>

<style scoped>
.error-border {
  border-color: #dc3545 !important;
}

.error-border:focus {
  border-color: #dc3545 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25) !important;
}

.error-message {
  color: #dc3545;
  font-size: 0.875rem;
  margin-top: 0.25rem;
}

.form-label {
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #495057;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.text-truncate {
  max-width: 200px;
}
</style>
